import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';
import type { OrderStatus } from '@wooconnect/shared';

const variantStyles = {
  default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  info: 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
} as const;

interface BadgeProps {
  label: string;
  variant?: keyof typeof variantStyles;
  size?: 'sm' | 'md';
  dot?: boolean;
  className?: string;
}

export function Badge({ label, variant = 'default', size = 'sm', dot, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
        variantStyles[variant],
        className,
      )}
    >
      {dot && (
        <span className={cn('h-1.5 w-1.5 rounded-full', {
          'bg-gray-500': variant === 'default',
          'bg-green-500': variant === 'success',
          'bg-yellow-500': variant === 'warning',
          'bg-red-500': variant === 'error',
          'bg-brand-500': variant === 'info',
          'bg-purple-500': variant === 'purple',
        })} />
      )}
      {label}
    </span>
  );
}

const statusVariantMap: Record<string, keyof typeof variantStyles> = {
  pending: 'warning',
  processing: 'info',
  'on-hold': 'warning',
  completed: 'success',
  cancelled: 'error',
  refunded: 'purple',
  failed: 'error',
};

export function OrderStatusBadge({ status, statusLabel, size = 'sm' }: { status: OrderStatus | string; statusLabel?: string; size?: 'sm' | 'md' }) {
  const { t } = useTranslation();
  const variant = statusVariantMap[status] ?? 'default';
  // Prefer app i18n for known statuses (respects app language); fall back to the
  // server-provided WC label for custom statuses, then the raw slug.
  const label = t(`orders.statuses.${status}`, statusLabel ?? status);
  return <Badge label={label} variant={variant} size={size} dot />;
}
